import { useMemo } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { F0Form, f0FormField, useF0Form } from '@factorialco/f0-react'
import {
  AtsDeleteApplicationPhaseDocument,
  AtsDeleteApplicationPhaseMutation,
  AtsGetHiringPipelinePhasesDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DeleteApplicationPhaseSkeleton } from './DeleteApplicationPhaseSkeleton'

interface Props {
  id: string
  jobId: string
  onClose: () => void
}

export const DeleteApplicationPhaseModal = ({ onClose, id, jobId }: Props) => {
  const [deleteAtsApplicationPhase, { loading }] = useMutation(AtsDeleteApplicationPhaseDocument, {
    extractorFn: (data: AtsDeleteApplicationPhaseMutation) =>
      data.atsMutations.deleteAtsApplicationPhase,
  })

  const { loading: loadingPhases, data: phasesData } = useQuery(
    AtsGetHiringPipelinePhasesDocument,
    {
      variables: { jobId },
    }
  )

  const { formRef, submit, isSubmitting } = useF0Form()

  const applicationPhases = useMemo(
    () => phasesData?.ats.applicationPhasesConnection.nodes || [],
    [phasesData]
  )

  const options = useMemo(
    () =>
      applicationPhases
        .filter((phase) => phase.id !== id)
        .map((phase) => ({
          label: phase.name,
          value: phase.id,
        })),
    [applicationPhases, id]
  )

  const schema = useMemo(
    () =>
      z.object({
        applicationPhaseId: f0FormField(
          z
            .string()
            .refine((value) => value !== '0', { message: i18n.t('common.option_selected.zero') }),
          {
            label: i18n.t('ats.job_posting_settings.hiring_pipeline.phase_input_label'),
            fieldType: 'select',
            options,
          }
        ),
      }),
    [options]
  )

  return (
    <Modal
      title={i18n.t(
        'ats.job_posting_settings.hiring_pipeline.delete_phase_with_applications_modal.title'
      )}
      description={i18n.t(
        'ats.job_posting_settings.hiring_pipeline.delete_phase_with_applications_modal.description'
      )}
      illustration="automations"
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" onClick={() => submit()} loading={loading || isSubmitting}>
            {i18n.t('common.delete')}
          </Button>
        </Box>
      }
    >
      {loadingPhases || !applicationPhases ? (
        <DeleteApplicationPhaseSkeleton />
      ) : (
        <F0Form
          formRef={formRef}
          name="ats-delete-application-phase"
          schema={schema}
          defaultValues={{ applicationPhaseId: '0' }}
          submitConfig={{ type: 'default', hideSubmitButton: true, hideActionBar: true }}
          onSubmit={async (data) => {
            const result = await deleteAtsApplicationPhase({
              variables: {
                id,
                alternativeApplicationPhaseId: data.applicationPhaseId,
              },
              update(cache) {
                cache.evict({ id: cache.identify({ id, __typename: 'AtsApplicationPhase' }) })
              },
            })

            const fieldErrors: Record<string, string> = {}
            const handled = handleResult(result, (errors) => {
              Object.assign(fieldErrors, errors)
            })

            if (handled.success) {
              app.setFlash({
                type: 'correct',
                message: i18n.t('common.removed_successfully'),
              })
              onClose()
              return { success: true }
            }

            return { success: false, errors: fieldErrors }
          }}
        />
      )}
    </Modal>
  )
}
